<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceTypeBadge',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    value: {
      type: String,
      required: false,
    },
    abbreviated: {
      type: Boolean,
      required: false,
    },
    kind: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    abbreviated: {
      normal: $fn.value.pgettext('InvoiceType|Abbrev', 'N'),
      storno: $fn.value.pgettext('InvoiceType|Abbrev', 'S'),
      correction: $fn.value.pgettext('InvoiceType|Abbrev', 'C'),
      proforma: $fn.value.pgettext('InvoiceType|Abbrev', 'PF'),
    },
    normal: $fn.value.pgettext('InvoiceType', 'Normal'),
    storno: $fn.value.pgettext('InvoiceType', 'Storno'),
    correction: $fn.value.pgettext('InvoiceType', 'Correction'),
    proforma: $fn.value.pgettext('InvoiceType', 'Pro Forma'),
  });
  const icons = ref({
    incoming: 'mdi:invoice-receive',
    outgoing: 'mdi:invoice',
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    icons: icons.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'icons' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, icons, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const colorCodes = computed(() => {
    return $config.value.invoice.types.colorCodes[props.value];
  });
  const invoiceKindIcon = computed(() => {
    if (loaded.value) {
      return icons.value[props.kind];
    }
    return null;
  });

  watch(
    () => props.value,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('value');
    }
  );
  watch(
    () => props.abbreviated,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('abbreviated');
    }
  );
  watch(
    () => props.kind,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('kind');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBadge
    no-box-shadow
    :title="abbreviated ? messages[value] : null"
    :class="`bg-${colorCodes.bg} text-${colorCodes.text} px-2`"
    v-if="colorCodes"
  >
    <ItpIcon :icon="invoiceKindIcon" v-if="invoiceKindIcon"></ItpIcon>
    {{ abbreviated ? messages.abbreviated[value] : messages[value] }}
  </ItpBadge>
</template>
